// src/pages/admin/CategoryManagementPage.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Modal, TextField, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import api from '../../api/index.js';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 500 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const CategoryManagementPage = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [editingCategory, setEditingCategory] = useState(null);
    const [error, setError] = useState('');

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (err) {
            setError('No se pudieron cargar las categorías.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleOpenCreate = () => {
        setEditingCategory(null);
        setFormData({ name: '', description: '' });
        setError('');
        setOpenModal(true);
    };

    const handleOpenEdit = (category) => {
        setEditingCategory(category);
        setFormData({ name: category.name || '', description: category.description || '' });
        setError('');
        setOpenModal(true);
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        setError('');
        try {
            if (editingCategory) {
                await api.put(`/categories/${editingCategory.id}`, formData);
            } else {
                await api.post('/categories', formData);
            }
            handleCloseModal();
            fetchCategories();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar la categoría.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro? Si esta categoría está en uso, no se podrá eliminar.')) {
            try {
                await api.delete(`/categories/${id}`);
                fetchCategories();
            } catch (err) {
                alert(err.response?.data?.message || 'Error al eliminar.');
            }
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'name', headerName: 'Nombre', width: 250 },
        { field: 'description', headerName: 'Descripción', flex: 1 },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleOpenEdit(params.row)}>Editar</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(params.row.id)}>Eliminar</Button>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ height: '85vh', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Gestión de Categorías</Typography>
                <Button variant="contained" onClick={handleOpenCreate}>Añadir Categoría</Button>
            </Box>
            {error && !loading && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <DataGrid
                rows={categories}
                columns={columns}
                loading={loading}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            />
            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={style}>
                    <Typography variant="h6">{editingCategory ? 'Editar Categoría' : 'Crear Categoría'}</Typography>
                    <TextField margin="normal" required fullWidth autoFocus label="Nombre" name="name" value={formData.name || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth multiline rows={3} label="Descripción" name="description" value={formData.description || ''} onChange={handleInputChange} />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>Guardar</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default CategoryManagementPage;